import { IeLynxObject } from "../../common/models/eLynxObject";
import { IKeyValuePair } from "../../common/models/KeyValuePair";

export enum CommTypeEnum {
    Direct = 0,
    Shared = 1
}

export enum CommPriorityEnum {
    Primary = 0,
    Secondary = 1
}

export enum AcmConnectionOptionEnum {
    // Primary Only
    PrimaryOnly = 0,
    // Secondary Only 
    SecondaryOnly = 1,
    // Switch to Secondary on Failure 
    SwitchToSecondaryOnFailure = 2
}

export enum AcmObjectTypeEnum {
    None = 0,
    Benchmark = 1,
    BSAP = 2,
    Camp = 3,
    ControlLogix = 4,
    Decasync = 5,
    DF1_ABTcp = 6,
    DNP3_FBx = 7,
    DNP3 = 8,
    Eagle = 9,
    FBNet = 10,
    FlowAutomation = 11,
    GE_SRTP = 12,
    HEXREPEATER = 13,
    HoneywellMercury = 14,
    Kimray = 15,
    Modbus_Alien2 = 16,
    Modbus_BlockHistory = 17,
    Modbus_BPWellControl = 18,
    Modbus_DynaPump = 19,
    Modbus_Enron = 20,
    Modbus_ETNG = 21,
    Modbus_FB3000 = 22,
    Modbus_KinderMorgan = 23,
    Modbus_LufkinSAM = 24,
    Modbus_MicroMVL = 25,
    Modbus_Omni = 26,
    Modbus_PCSPlungerLift = 27,
    Modbus_PCSProsoft = 28,
    Modbus = 29,
    ModbusSCADAPack = 30,
    ModbusScanner1150 = 31,
    NGCTT = 32,
    ReynoldsLVC = 33,
    ROC = 34,
    Scancom = 35,
    SpiritFlowX = 36,
    TGP = 37,
    Totalflow = 38,
    TcpIP = 39,
    TcpListen = 40,
    Folder = 41,
    // 5 New Protocol additions for the 6/3/20 - Update to ACM
    SiemensS7 = 42,
    Dresser = 43,
    // ACM just added the word NEW after the name
    HoneywellMercury_NEW = 44,
    ROMETAdEM = 45,
    TeledyneCA = 46
}

export enum PlatformPollingServerTypeEnum {
    ACM = 0,
    Kepware = 1
}

// Propterties that are NULL will not be set.
export interface IAcmBaseParams {

    acmObjectType?: AcmObjectTypeEnum;

    /// <summary>
    /// Optional Description for storing additional information for a user
    ///     to identify the reason for this object.
    /// </summary>
    description?: string;

    /// <summary>
    /// Optional name when finding by name, creating a new object, or updating the name of an object.
    /// </summary>
    displayText?: string;

    /// <summary>
    /// Optional Enabled flag when creating a new object or updating the status of an object. 
    ///     If the Enabled flag is not specified when creating an object then the object will
    ///     be created as Enabled = false. If the Enabled flag is not specified when updating
    ///     an object then the Enabled status is not changed.
    /// </summary>
    enabled?: boolean;

    /// <summary>
    /// Optional ID used when Updating an object.
    /// </summary>
    id?: number;

    /// <summary>
    /// Optional Parent ID. Only used when retrieving multiple Objects or when creating 
    ///     an object that needs a Parent ID.
    /// When retrieving multiple objects if the Parent ID is not supplied then 
    ///     all Objects for the Polling Server are returned.
    /// </summary>
    parentID?: number;

    /// <summary>
    /// The Polling Server Name
    /// </summary>
    pollingServerName?: string;

    /// <summary>
    /// Use the standard eLynx Subfolder below the specified Parent ID as 
    ///     the Object's real Parent.
    ///     At this time this flag is only used for creating TCP IP and TCP Listen objects.
    ///     
    /// Standard Subfolders    
    ///     Communications Objects:         _CommPorts
    ///     Communication Settings Object:  _Comm Settings
    ///     
    /// /// </summary>
    useStandardSubfolder?: boolean;
}

// Properties that are null will not be set. Properties that are IDs
//  that are set to 0 will be set to undefined in ACM
export interface IAcmProtocolParams extends IAcmBaseParams {
    /// <summary>
    /// Flag that determines whether ACM will attempt to auto recover after a connection issue?
    /// </summary>
    autoRecover?: boolean;
    /// <summary>
    /// The Primary and Secondary Connection options. The options are
    ///     Primary Only
    ///         PrimaryOnly = 0,
    ///     Secondary Only 
    ///         SecondaryOnly = 1,
    ///     Switch to Secondary on Failure 
    ///         SwitchToSecondaryOnFailure = 2
    /// </summary>
    connectionOption?: AcmConnectionOptionEnum;
    /// <summary>
    /// The Primary connection ID for either a TCP IP Connection Object
    ///     or TCP Listen Object. If this is a TCP Listen Object then
    ///     the PrimaryConnectionString must also be set with the IP Address and Port.
    /// </summary>
    primaryConnectionID?: number;
    /// <summary>
    /// The Primary IP address and Port of the format 192.168.1.50:5110. This must
    ///     be set for a TCP Listen. It is optional for a TCP IP Connection and will
    ///     be an override of the TCP IP Connection and Port in the 
    ///     PrimaryConnectionID Object
    /// </summary>
    primaryConnectionString?: string;
    /// <summary>
    /// The Primary Connection Settings Object ID. Support says 
    ///     they currently do not use this setting, they set it
    ///     in the Protocol instead.
    /// </summary>
    primaryConnectionSettingsID?: number;
    /// <summary>
    /// How many times the Secondary Connection will be attempted before failure?
    /// </summary>  
    secondaryAttempts?: number;
    /// <summary>
    /// The Secondary connection ID for either a TCP IP Connection Object
    ///     or TCP Listen Object. If this is a TCP Listen Object then
    ///     the SecondaryConnectionString must also be set with the IP Address and Port.
    ///     This setting is used as a Backup for the Primary Connection?
    /// </summary>
    secondaryConnectionID?: number;
    /// <summary>
    /// The Secondary IP address and Port of the format 192.168.1.50:5110. This must
    ///     be set for a TCP Listen. It is optional for a TCP IP Connection and will
    ///     be an override of the TCP IP Connection and Port in the 
    ///     SecondaryConnectionID Object
    ///     This setting is used as a Backup for the Primary Connection?
    /// </summary>
    secondaryConnectionString?: string;
    /// <summary>
    /// The Secondary Connection Settings Object ID. Support says 
    ///     they currently do not use this setting, they set it
    ///     in the Protocol instead.
    ///     This setting is used as a Backup for the Primary Connection?
    /// </summary>
    secondaryConnectionSettingsID?: number;
}

export interface IAcmTcpParams extends IAcmBaseParams {
    connectionSettingsID?: number;
}

export interface IAcmTcpIpParams extends IAcmTcpParams {
    host1TcpIpAddress?: string;
    host2TcpIpAddress?: string;
    host1TcpIpPort?: number;
    host2TcpIpPort?: number;
}

export interface IAcmTcpListenParams extends IAcmTcpParams {
    maxSessions?: number;
}

export interface IAcmBaseObject {
    acmObjectType: AcmObjectTypeEnum;
    createDate: Date;
    description: string;
    displayText: string;
    enabled: boolean;
    fullName: string;
    id: number;
    modifyDate: Date;
    name: string;
    objectTypeName: string;
    parentID: number;
    pollingServerName: string;

    // UI Only
    acmObjectTypeText: string;
}

export interface IAcmProtocolObjectProperty {
    name: string;
    value: string;
}

export interface IAcmProtocolObject extends IAcmBaseObject {
    address: string;
    autoRecover: boolean;
    connectionOption: AcmConnectionOptionEnum;
    connectionOptionText: string;
    primaryConnectionID: number;
    primaryConnectionString: string;
    primaryConnectionSettingsID: number;
    secondaryConnectionID: number;
    secondaryAttempts: number;
    secondaryConnectionString: string;
    secondaryConnectionSettingsID: number;
    properties?: Array<IAcmProtocolObjectProperty>;

    // UI Only
    tcpIpPrimaryConnectionObject: IAcmTcpIpConnectionObject;
    tcpListenPrimaryConnectionObject: IAcmTcpListenConnectionObject;
    tcpIpSecondaryConnectionObject: IAcmTcpIpConnectionObject;
    tcpListenSecondaryConnectionObject: IAcmTcpListenConnectionObject;
    hasPrimaryConnection: boolean;
    hasSecondaryConnection: boolean;

    ipAddress1: string;
    port1: number;
    ipAddress2: string;
    port2: number;

    devices?: IeLynxObject[];
    wells?: IeLynxObject[];
}

export interface IAcmConnectionObject extends IAcmBaseObject {

    /// <summary>
    /// The Connection Settings Object ID. Support says 
    ///     they currently do not use this setting in the TCP IP or Listen
    ///     Objects, they set it in the Protocol instead.
    /// </summary>
    connectionSettingsID: number;
    protocolUsage: Array<IAcmProtocolObject>;

    iccid?: string;
    cycletoDateUsage?: string;

    // ui properties
    // acmCommType?: CommTypeEnum; // don't really need this
    devices?: IeLynxObject[];
    wells?: IeLynxObject[];
    priority?: CommPriorityEnum;
}

export interface IAcmTcpIpConnectionObject extends IAcmConnectionObject {
    host1TcpIpAddress: string;
    host2TcpIpAddress: string;
    host1TcpIpPort: number;
    host2TcpIpPort: number;
    selectedHost: number;

    // ui properties
    ipAddress1: string;
    ipAddress2: string;
}

export interface IAcmTcpListenConnectionObject extends IAcmConnectionObject {
    maxSessions: number;
}

export interface IAcmDeviceProtocolObjectUsage {

    protocolObjects: IAcmProtocolObject[];
    ownedMap: IKeyValuePair<number, boolean>[];
    devices: IeLynxObject[];
    //wells: IeLynxObject[];

    // ui properties
    object?: IeLynxObject;
    commObjects?: IAcmConnectionObject[];
}

export interface IAcmProtocolObjectUsage {
    protocolObject: IAcmProtocolObject;
    devices: IeLynxObject[];
    //wells: IeLynxObject[];
}



export interface IelmmDevice {
    accessKey: string;
    accessPort: number;
    apnStatus: string;
    atStatus: string;
    deviceId: string;
    deviceModel: string;
    displayName: string;
    elmmVersion: string;
    historyDt: string;
    imei: string;
    isEnabled: boolean;
    ipAddress: string;
    isPublic: boolean;
    lastUpdateHours: number;
    serialNumber: string;
    servingNetworkOp: string;
    signalStrength: number;
    simId: string;
    softwareVersion: string;
    totalBytes: string;
    dataplanName: string;
    dataplanSizeInBytes: string;
    dataplanUsage: string;
}

export interface IeLmmNetInfoValue  {
        tagName: string;
        value: string;
}


export interface IeLmmNetInfo {
        port_forwarding: IeLmmNetInfoValue[];
        ethernet: IeLmmNetInfoValue[];
        signal: IeLmmNetInfoValue[];
}

export interface IAcmConnectionObjectUsage {
    commObject: IAcmConnectionObject;
    iccid: string;
    cycletoDateUsage: number;
    devices: IeLynxObject[];

    elmmDevice: IelmmDevice | undefined;
    elmmNetInfo: IeLmmNetInfo | undefined;

    //wells: IeLynxObject[];
}

export interface ICIDRAddress {
    id: number;
    address: string;
}

export interface IPlatformPollingObjectConnection {
    id: number,
    connectionEndpoint: string,
    pollingServerType: PlatformPollingServerTypeEnum,
    customerID: number,
    rootID: number
}

export interface ICommDeviceModel extends IeLynxObject {
    canDemandScan: boolean;
}